<template>
  <div>
    <!-- Breadcrumbs -->
    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-4 sm:p-6">
      <!-- Main Container -->
      <div class="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
        <!-- Title Bar -->
        <div class="bg-blue-600 text-white p-4">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0">
                <v-icon name="co-truck" class="text-blue-600" scale="1.5" />
              </div>
              <div>
                <h1 class="text-lg sm:text-xl font-bold">{{ $t('pv.veicoli.title') }}</h1>
                <p class="text-xs text-blue-100 opacity-90 mt-0.5">
                  {{ $t('pv.veicoli.subtitle') }}
                </p>
              </div>
            </div>

            <!-- Top Action Buttons -->
            <div class="flex flex-wrap items-center gap-2">
              <button
                @click="openTemplatesModal"
                type="button"
                class="px-3 py-1.5 bg-blue-700 hover:bg-blue-800 text-white rounded text-xs font-semibold flex items-center gap-1.5 transition-colors"
                :title="$t('pv.veicoli.btn_templates')"
              >
                <v-icon name="hi-lightning-bolt" scale="0.85" />
                <span>{{ $t('pv.veicoli.btn_templates') }}</span>
              </button>

              <button
                @click="openCreateModal"
                type="button"
                class="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-bold flex items-center gap-1.5 shadow transition-all"
              >
                <v-icon name="hi-plus" scale="0.9" />
                <span>{{ $t('pv.veicoli.btn_new') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Info & Routing Explanation Banner -->
        <div class="bg-blue-50 border-b border-blue-200 p-4 sm:p-5">
          <div class="flex items-start gap-3">
            <div class="p-1.5 bg-blue-100 rounded-lg text-blue-700 shrink-0 mt-0.5">
              <v-icon name="bi-info-circle" scale="1.1" />
            </div>
            <div class="flex-1 text-xs">
              <div class="font-bold text-blue-900 mb-0.5 uppercase tracking-wide">
                {{ $t('pv.veicoli.routing_impact_title') }}
              </div>
              <p class="text-blue-800 leading-relaxed text-[11px] sm:text-xs">
                {{ $t('pv.veicoli.routing_impact_desc') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Summary Metric Cards -->
        <div class="p-3 sm:p-4 bg-gray-50 border-b border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <div class="bg-white p-2.5 sm:p-3 rounded-lg border border-gray-200 shadow-sm">
            <div class="text-[10px] uppercase font-bold text-gray-500 mb-1">
              {{ $t('pv.veicoli.stat_total') }}
            </div>
            <div class="text-xl sm:text-2xl font-black text-blue-700 flex items-center justify-between">
              <span>{{ vehicles.length }}</span>
              <v-icon name="co-truck" scale="1.1" class="text-gray-300" />
            </div>
          </div>

          <div class="bg-white p-2.5 sm:p-3 rounded-lg border border-gray-200 shadow-sm">
            <div class="text-[10px] uppercase font-bold text-gray-500 mb-1">
              {{ $t('pv.veicoli.stat_heavy') }}
            </div>
            <div class="text-xl sm:text-2xl font-black text-orange-600 flex items-center justify-between">
              <span>{{ countHeavyVehicles }}</span>
              <v-icon name="fa-truck-loading" scale="1.1" class="text-gray-300" />
            </div>
          </div>

          <div class="bg-white p-2.5 sm:p-3 rounded-lg border border-gray-200 shadow-sm">
            <div class="text-[10px] uppercase font-bold text-gray-500 mb-1">
              {{ $t('pv.veicoli.stat_light') }}
            </div>
            <div class="text-xl sm:text-2xl font-black text-green-600 flex items-center justify-between">
              <span>{{ countLightVehicles }}</span>
              <v-icon name="md-localshipping-twotone" scale="1.1" class="text-gray-300" />
            </div>
          </div>

          <div class="bg-white p-2.5 sm:p-3 rounded-lg border border-gray-200 shadow-sm">
            <div class="text-[10px] uppercase font-bold text-gray-500 mb-1">
              {{ $t('pv.veicoli.stat_special') }}
            </div>
            <div class="text-xl sm:text-2xl font-black text-purple-600 flex items-center justify-between">
              <span>{{ countSpecialVehicles }}</span>
              <v-icon name="hi-adjustments" scale="1.1" class="text-gray-300" />
            </div>
          </div>
        </div>

        <!-- Filter & Search Toolbar (Responsive & Full-Width) -->
        <div class="p-3 sm:p-4 border-b border-gray-200 bg-white">
          <div class="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
            <!-- Search & Filters Container -->
            <div class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 flex-1 w-full">
              <!-- Search Input (takes priority and never shrinks excessively) -->
              <div class="relative flex-1 min-w-[220px] w-full sm:w-auto">
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="$t('pv.veicoli.search_placeholder')"
                  class="w-full pl-8 pr-8 py-2 text-xs border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <v-icon name="co-search" class="absolute left-2.5 top-2.5 text-gray-400" scale="0.8" />
                <button
                  v-if="searchQuery"
                  @click="searchQuery = ''"
                  class="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 p-0.5"
                >
                  <v-icon name="co-x" scale="0.8" />
                </button>
              </div>

              <!-- Filter Selects -->
              <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
                <select
                  v-model="filterType"
                  class="flex-1 sm:flex-initial text-xs border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2"
                >
                  <option value="">{{ $t('pv.veicoli.filter_all_types') }}</option>
                  <option v-for="t in vehicleTypeOptions" :key="t.value" :value="t.value">
                    {{ t.label }}
                  </option>
                </select>

                <select
                  v-model="filterCarrier"
                  class="flex-1 sm:flex-initial text-xs border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2"
                >
                  <option value="">{{ $t('pv.veicoli.filter_all_carriers') }}</option>
                  <option v-for="c in carriersList" :key="c" :value="c">
                    {{ c }}
                  </option>
                </select>

                <button
                  v-if="searchQuery || filterType || filterCarrier"
                  @click="resetFilters"
                  class="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 shrink-0"
                  type="button"
                >
                  Reset
                </button>
              </div>
            </div>

            <!-- View Mode Switch (Table view disabled on mobile) -->
            <div class="flex items-center justify-end gap-2 shrink-0 pt-1 lg:pt-0">
              <div class="flex bg-gray-100 p-0.5 rounded border border-gray-200">
                <button
                  type="button"
                  @click="viewMode = 'cards'"
                  :class="viewMode === 'cards' ? 'bg-white shadow-sm text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-700'"
                  class="px-2.5 py-1 text-xs rounded transition-all flex items-center gap-1"
                  :title="$t('pv.veicoli.view_cards')"
                >
                  <v-icon name="md-tableview" scale="0.8" />
                  <span class="hidden sm:inline">{{ $t('pv.veicoli.view_cards') }}</span>
                </button>
                <button
                  type="button"
                  @click="viewMode = 'table'"
                  :class="viewMode === 'table' ? 'bg-white shadow-sm text-blue-700 font-bold' : 'text-gray-500 hover:text-gray-700'"
                  class="hidden sm:flex px-2.5 py-1 text-xs rounded transition-all items-center gap-1"
                  :title="$t('pv.veicoli.view_table')"
                >
                  <v-icon name="bi-list-ul" scale="0.8" />
                  <span class="hidden sm:inline">{{ $t('pv.veicoli.view_table') }}</span>
                </button>
              </div>

              <button
                type="button"
                @click="confirmResetFleet"
                class="text-xs text-gray-400 hover:text-gray-600 p-1.5 rounded hover:bg-gray-100"
                :title="$t('pv.veicoli.btn_reset_default')"
              >
                <v-icon name="hi-refresh" scale="0.8" />
              </button>
            </div>
          </div>
        </div>

        <!-- Vehicles Content: Empty State -->
        <div v-if="filteredVehicles.length === 0" class="p-8 sm:p-12 text-center text-gray-500">
          <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
            <v-icon name="co-truck" scale="2" />
          </div>
          <h3 class="text-base font-bold text-gray-800 mb-1">{{ $t('pv.veicoli.no_vehicles') }}</h3>
          <p class="text-xs text-gray-500 mb-4 max-w-md mx-auto">{{ $t('pv.veicoli.no_vehicles_desc') }}</p>
          <button
            @click="openCreateModal"
            type="button"
            class="px-4 py-2 bg-blue-600 text-white rounded text-xs font-bold hover:bg-blue-700 transition-colors inline-flex items-center gap-1"
          >
            <v-icon name="hi-plus" scale="0.8" /> {{ $t('pv.veicoli.btn_new') }}
          </button>
        </div>

        <!-- CARDS VIEW (Responsive with no badge collision) -->
        <div v-else-if="effectiveViewMode === 'cards'" class="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
          <div
            v-for="vehicle in filteredVehicles"
            :key="vehicle.id"
            class="bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
            :class="vehicle.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60 bg-gray-50'"
          >
            <!-- Card Header: Clean responsive stacking -->
            <div class="p-4 sm:p-5 border-b border-gray-100 bg-gray-50">
              <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <!-- Left: Plate & Vehicle Name -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between sm:justify-start gap-2 mb-1.5 flex-wrap">
                    <!-- European Style License Plate -->
                    <div class="inline-flex items-center bg-white border-2 border-gray-800 rounded px-2 py-0.5 shadow-xs shrink-0">
                      <span class="bg-blue-700 text-white font-bold text-[8px] px-1 py-0.5 rounded-l-xs mr-1.5 flex items-center">EU</span>
                      <span class="font-mono font-black text-xs text-gray-900 tracking-wider uppercase">{{ vehicle.plate || 'SCONOSCIUTA' }}</span>
                      <span class="bg-blue-700 text-white font-bold text-[8px] px-1 py-0.5 rounded-r-xs ml-1.5 flex items-center">I</span>
                    </div>

                    <!-- Type Tag on Mobile screens -->
                    <span
                      class="sm:hidden inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0"
                      :class="getTypeBadgeClass(vehicle.type)"
                    >
                      {{ getTypeLabel(vehicle.type) }}
                    </span>
                  </div>

                  <h3 class="text-sm font-bold text-gray-900 truncate" :title="vehicle.name">
                    {{ vehicle.name }}
                  </h3>
                  <div class="text-xs text-gray-500 truncate" :title="vehicle.brand_model">
                    {{ vehicle.brand_model || 'Modello non specificato' }}
                  </div>
                </div>

                <!-- Right / Desktop: Type Badge & Carrier -->
                <div class="hidden sm:block text-right shrink-0">
                  <span
                    class="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                    :class="getTypeBadgeClass(vehicle.type)"
                  >
                    {{ getTypeLabel(vehicle.type) }}
                  </span>
                  <div v-if="vehicle.carrier" class="text-[10px] font-semibold text-blue-700 mt-1 flex items-center justify-end gap-1">
                    <v-icon name="co-truck" scale="0.65" />
                    <span class="truncate max-w-[120px]">{{ vehicle.carrier }}</span>
                  </div>
                </div>

                <!-- Carrier on Mobile -->
                <div v-if="vehicle.carrier" class="sm:hidden text-[11px] font-semibold text-blue-700 flex items-center gap-1 pt-0.5">
                  <v-icon name="co-truck" scale="0.65" />
                  <span>{{ vehicle.carrier }}</span>
                </div>
              </div>
            </div>

            <!-- Card Body: Dimensions & Weights -->
            <div class="p-4 sm:p-5 space-y-3 flex-1">
              <!-- Physical Dimensions (4 main metric boxes) -->
              <div class="grid grid-cols-4 gap-1.5 text-center">
                <div class="bg-blue-50/80 border border-blue-100 p-1.5 sm:p-2 rounded">
                  <span class="block text-[9px] uppercase font-bold text-blue-600">Altezza</span>
                  <span class="text-xs font-black text-gray-900">{{ formatCentimetersToMeters(vehicle.height) }}</span>
                </div>
                <div class="bg-blue-50/80 border border-blue-100 p-1.5 sm:p-2 rounded">
                  <span class="block text-[9px] uppercase font-bold text-blue-600">Larghezza</span>
                  <span class="text-xs font-black text-gray-900">{{ formatCentimetersToMeters(vehicle.width) }}</span>
                </div>
                <div class="bg-blue-50/80 border border-blue-100 p-1.5 sm:p-2 rounded">
                  <span class="block text-[9px] uppercase font-bold text-blue-600">Lunghezza</span>
                  <span class="text-xs font-black text-gray-900">{{ formatCentimetersToMeters(vehicle.length) }}</span>
                </div>
                <div class="bg-blue-50/80 border border-blue-100 p-1.5 sm:p-2 rounded">
                  <span class="block text-[9px] uppercase font-bold text-blue-600">PTT Max</span>
                  <span class="text-xs font-black text-gray-900">{{ formatKgToTons(vehicle.gross_weight) }}</span>
                </div>
              </div>

              <!-- Secondary Details Row -->
              <div class="text-xs text-gray-600 space-y-1.5 pt-1 border-t border-gray-100">
                <div class="flex justify-between items-center text-[11px]">
                  <span class="text-gray-500">Max Carico per Asse:</span>
                  <span class="font-semibold text-gray-800">
                    {{ vehicle.weight_per_axle ? formatKgToTons(vehicle.weight_per_axle) : '---' }}
                    ({{ vehicle.axle_count || 2 }} assi)
                  </span>
                </div>
                <div class="flex justify-between items-center text-[11px]">
                  <span class="text-gray-500">Portata Utile / Volume:</span>
                  <span class="font-semibold text-gray-800">
                    {{ vehicle.payload_capacity ? vehicle.payload_capacity + ' kg' : '---' }}
                    <span v-if="vehicle.cargo_volume"> | {{ vehicle.cargo_volume }} m³</span>
                  </span>
                </div>
                <div v-if="vehicle.trailer_length || vehicle.kpra_length" class="flex justify-between items-center text-[11px]">
                  <span class="text-gray-500">Rimorchio / KPRA:</span>
                  <span class="font-semibold text-gray-800">
                    {{ vehicle.trailer_length ? formatCentimetersToMeters(vehicle.trailer_length) : '-' }} /
                    {{ vehicle.kpra_length ? formatCentimetersToMeters(vehicle.kpra_length) : '-' }}
                  </span>
                </div>
              </div>

              <!-- Feature Tags & Restrizioni -->
              <div class="flex flex-wrap gap-1 pt-1.5">
                <span v-if="vehicle.has_tail_lift" class="bg-emerald-100 text-emerald-800 text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" title="Sponda Idraulica">
                  ✓ Sponda
                </span>
                <span v-if="vehicle.has_adr" class="bg-amber-100 text-amber-900 text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5" title="Merci Pericolose ADR">
                  ⚠️ ADR ({{ vehicle.adr_tunnel_category || 'Tutti' }})
                </span>
                <span v-if="vehicle.trailer_count > 0" class="bg-indigo-100 text-indigo-800 text-[9px] font-bold px-1.5 py-0.5 rounded" title="Rimorchio presente">
                  + {{ vehicle.trailer_count }} Rimorchio ({{ vehicle.trailer_axle_count || 0 }} assi)
                </span>
                <span v-if="vehicle.euro_class" class="bg-gray-100 text-gray-700 text-[9px] font-bold px-1.5 py-0.5 rounded">
                  {{ vehicle.euro_class }}
                </span>
              </div>

              <!-- Operational Notes -->
              <div v-if="vehicle.notes" class="text-[11px] text-gray-500 italic bg-gray-50 p-1.5 rounded border border-gray-100 truncate" :title="vehicle.notes">
                💬 {{ vehicle.notes }}
              </div>
            </div>

            <!-- Card Footer: Actions -->
            <div class="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
              <div class="flex items-center gap-1">
                <button
                  @click="duplicateVehicle(vehicle)"
                  type="button"
                  class="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded transition-colors"
                  title="Duplica veicolo"
                >
                  <v-icon name="co-copy" scale="0.85" />
                </button>
                <button
                  @click="openEditModal(vehicle)"
                  type="button"
                  class="px-3 py-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 rounded text-xs font-bold transition-colors flex items-center gap-1"
                >
                  <v-icon name="bi-pencil" scale="0.75" />
                  <span>Modifica</span>
                </button>
                <button
                  @click="confirmDelete(vehicle)"
                  type="button"
                  class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Elimina veicolo"
                >
                  <v-icon name="co-trash" scale="0.85" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- TABLE VIEW (Desktop / Tablet only) -->
        <div v-else-if="effectiveViewMode === 'table'" class="hidden sm:block overflow-x-auto">
          <table class="min-w-full border-collapse text-left text-xs">
            <thead class="bg-gray-100 text-gray-700 uppercase font-bold text-[10px] border-b">
              <tr>
                <th class="p-3 w-12 text-center">Stato</th>
                <th class="p-3 min-w-[110px]">{{ $t('pv.veicoli.col_plate') }}</th>
                <th class="p-3 min-w-[160px]">{{ $t('pv.veicoli.col_name') }}</th>
                <th class="p-3 min-w-[120px]">{{ $t('pv.veicoli.col_type') }}</th>
                <th class="p-3 min-w-[120px]">{{ $t('pv.veicoli.col_carrier') }}</th>
                <th class="p-3 min-w-[140px] text-center">{{ $t('pv.veicoli.col_dimensions') }}</th>
                <th class="p-3 min-w-[120px] text-center">{{ $t('pv.veicoli.col_weight') }}</th>
                <th class="p-3 min-w-[120px] text-center">{{ $t('pv.veicoli.col_payload') }}</th>
                <th class="p-3 min-w-[120px]">{{ $t('pv.veicoli.col_features') }}</th>
                <th class="p-3 w-28 text-right">{{ $t('pv.veicoli.col_actions') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="vehicle in filteredVehicles"
                :key="vehicle.id"
                class="hover:bg-blue-50/50 transition-colors"
                :class="{ 'opacity-60 bg-gray-50': !vehicle.is_active }"
              >
                <!-- Status -->
                <td class="p-3 text-center">
                  <span
                    class="inline-block w-2.5 h-2.5 rounded-full"
                    :class="vehicle.is_active ? 'bg-green-500' : 'bg-gray-300'"
                    :title="vehicle.is_active ? 'Attivo' : 'Inattivo'"
                  ></span>
                </td>

                <!-- Plate -->
                <td class="p-3 font-mono font-bold text-gray-900 whitespace-nowrap">
                  <span class="bg-gray-100 border border-gray-300 px-1.5 py-0.5 rounded text-[11px]">
                    {{ vehicle.plate || '---' }}
                  </span>
                </td>

                <!-- Name & Model -->
                <td class="p-3">
                  <div class="font-bold text-gray-900">{{ vehicle.name }}</div>
                  <div class="text-[10px] text-gray-500">{{ vehicle.brand_model }}</div>
                </td>

                <!-- Type -->
                <td class="p-3">
                  <span
                    class="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                    :class="getTypeBadgeClass(vehicle.type)"
                  >
                    {{ getTypeLabel(vehicle.type) }}
                  </span>
                </td>

                <!-- Carrier -->
                <td class="p-3 font-medium text-blue-700">
                  {{ vehicle.carrier || '---' }}
                </td>

                <!-- Dimensions (L x P x H) -->
                <td class="p-3 text-center whitespace-nowrap font-mono font-semibold text-gray-800">
                  {{ formatCentimetersToMeters(vehicle.length) }} x {{ formatCentimetersToMeters(vehicle.width) }} x {{ formatCentimetersToMeters(vehicle.height) }}
                </td>

                <!-- Weight / Axle -->
                <td class="p-3 text-center whitespace-nowrap">
                  <div class="font-bold text-gray-900">{{ formatKgToTons(vehicle.gross_weight) }}</div>
                  <div class="text-[10px] text-gray-500">Asse: {{ vehicle.weight_per_axle ? formatKgToTons(vehicle.weight_per_axle) : '---' }}</div>
                </td>

                <!-- Payload / Volume -->
                <td class="p-3 text-center whitespace-nowrap">
                  <div class="font-semibold text-gray-900">{{ vehicle.payload_capacity ? vehicle.payload_capacity + ' kg' : '---' }}</div>
                  <div class="text-[10px] text-gray-500">{{ vehicle.cargo_volume ? vehicle.cargo_volume + ' m³' : '' }}</div>
                </td>

                <!-- Features -->
                <td class="p-3">
                  <div class="flex flex-wrap gap-1">
                    <span v-if="vehicle.has_tail_lift" class="bg-emerald-100 text-emerald-800 text-[8px] font-bold px-1 rounded">Sponda</span>
                    <span v-if="vehicle.has_adr" class="bg-amber-100 text-amber-900 text-[8px] font-bold px-1 rounded">ADR</span>
                    <span v-if="vehicle.trailer_count > 0" class="bg-indigo-100 text-indigo-800 text-[8px] font-bold px-1 rounded">Rimorchio</span>
                    <span v-if="vehicle.euro_class" class="bg-gray-100 text-gray-700 text-[8px] font-bold px-1 rounded">{{ vehicle.euro_class }}</span>
                  </div>
                </td>

                <!-- Actions -->
                <td class="p-3 text-right whitespace-nowrap space-x-1">
                  <button
                    @click="duplicateVehicle(vehicle)"
                    class="p-1 text-gray-500 hover:text-blue-600 rounded"
                    title="Duplica"
                  >
                    <v-icon name="co-copy" scale="0.8" />
                  </button>
                  <button
                    @click="openEditModal(vehicle)"
                    class="p-1 text-gray-500 hover:text-blue-600 rounded"
                    title="Modifica"
                  >
                    <v-icon name="bi-pencil" scale="0.8" />
                  </button>
                  <button
                    @click="confirmDelete(vehicle)"
                    class="p-1 text-gray-400 hover:text-red-600 rounded"
                    title="Elimina"
                  >
                    <v-icon name="co-trash" scale="0.8" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- MODAL: CREA / MODIFICA VEICOLO -->
    <!-- ========================================================================= -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      @click.self="closeEditModal"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[92vh]">
        <!-- Modal Header -->
        <div class="bg-blue-600 text-white p-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <v-icon name="co-truck" scale="1.2" />
            <h2 class="text-base sm:text-lg font-bold">
              {{ isEditing ? $t('pv.veicoli.modal_edit_title', { name: form.name || form.plate }) : $t('pv.veicoli.modal_new_title') }}
            </h2>
          </div>
          <button @click="closeEditModal" class="text-white hover:text-gray-200 transition-colors p-1">
            <v-icon name="co-x" scale="1.2" />
          </button>
        </div>

        <!-- Modal Tabs -->
        <div class="flex border-b border-gray-200 bg-gray-50 px-3 sm:px-4 pt-2 overflow-x-auto">
          <button
            type="button"
            @click="activeTab = 'general'"
            :class="activeTab === 'general' ? 'border-blue-600 text-blue-600 font-bold bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'"
            class="px-3 sm:px-4 py-2 text-xs border-b-2 rounded-t-lg transition-all whitespace-nowrap"
          >
            {{ $t('pv.veicoli.tab_general') }}
          </button>
          <button
            type="button"
            @click="activeTab = 'dimensions'"
            :class="activeTab === 'dimensions' ? 'border-blue-600 text-blue-600 font-bold bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'"
            class="px-3 sm:px-4 py-2 text-xs border-b-2 rounded-t-lg transition-all flex items-center gap-1 whitespace-nowrap"
          >
            <span>{{ $t('pv.veicoli.tab_dimensions') }}</span>
            <span class="bg-blue-100 text-blue-800 text-[9px] px-1.5 py-0.2 rounded-full font-bold">Routing</span>
          </button>
          <button
            type="button"
            @click="activeTab = 'capacity'"
            :class="activeTab === 'capacity' ? 'border-blue-600 text-blue-600 font-bold bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'"
            class="px-3 sm:px-4 py-2 text-xs border-b-2 rounded-t-lg transition-all whitespace-nowrap"
          >
            {{ $t('pv.veicoli.tab_capacity') }}
          </button>
          <button
            type="button"
            @click="activeTab = 'preview'"
            :class="activeTab === 'preview' ? 'border-blue-600 text-blue-600 font-bold bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'"
            class="px-3 sm:px-4 py-2 text-xs border-b-2 rounded-t-lg transition-all flex items-center gap-1 whitespace-nowrap"
          >
            <v-icon name="co-map" scale="0.75" />
            <span>{{ $t('pv.veicoli.tab_preview') }}</span>
          </button>
        </div>

        <!-- Modal Form Body -->
        <div class="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          <!-- Quick Preset Selection Bar inside Modal -->
          <div class="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap items-center justify-between gap-2">
            <span class="text-xs font-bold text-blue-900 flex items-center gap-1">
              <v-icon name="hi-lightning-bolt" scale="0.8" class="text-blue-600" />
              Carica Sagoma Rapida:
            </span>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="tpl in presetTemplates"
                :key="tpl.id"
                type="button"
                @click="applyTemplateToForm(tpl)"
                class="px-2 py-1 bg-white hover:bg-blue-100 text-blue-700 border border-blue-300 rounded text-[11px] font-semibold transition-colors"
              >
                {{ tpl.label }}
              </button>
            </div>
          </div>

          <!-- TAB 1: DATI GENERALI -->
          <div v-show="activeTab === 'general'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_name') }} *
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  required
                  :placeholder="$t('pv.veicoli.field_name_placeholder')"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_plate') }} *
                </label>
                <input
                  v-model="form.plate"
                  type="text"
                  required
                  :placeholder="$t('pv.veicoli.field_plate_placeholder')"
                  class="w-full text-xs p-2 uppercase font-mono font-bold border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_type') }} *
                </label>
                <select
                  v-model="form.type"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option v-for="opt in vehicleTypeOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_carrier') }}
                </label>
                <select
                  v-model="form.carrier"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{{ $t('pv.veicoli.field_carrier_placeholder') }}</option>
                  <option v-for="c in carriersList" :key="c" :value="c">
                    {{ c }}
                  </option>
                </select>
              </div>

              <div class="col-span-1 md:col-span-2">
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_brand_model') }}
                </label>
                <input
                  v-model="form.brand_model"
                  type="text"
                  :placeholder="$t('pv.veicoli.field_brand_model_placeholder')"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div class="col-span-1 md:col-span-2">
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  {{ $t('pv.veicoli.field_notes') }}
                </label>
                <textarea
                  v-model="form.notes"
                  rows="2"
                  :placeholder="$t('pv.veicoli.field_notes_placeholder')"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>

              <div class="col-span-1 md:col-span-2">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="form.is_active"
                    type="checkbox"
                    class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span class="text-xs font-bold text-gray-800">{{ $t('pv.veicoli.field_is_active') }}</span>
                </label>
              </div>
            </div>
          </div>

          <!-- TAB 2: DIMENSIONI E PESI (Centimetri e Kg con conversioni in m e t) -->
          <div v-show="activeTab === 'dimensions'" class="space-y-4">
            <div class="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              ⚠️ <strong>Parametri Truck Routing (HERE / Google Maps):</strong> I valori sono salvati in <strong>centimetri</strong> e <strong>chilogrammi</strong> per garantire la massima precisione nei calcoli di transito e sagoma.
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Altezza (cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  📏 {{ $t('pv.veicoli.field_height') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.height"
                    type="number"
                    step="1"
                    min="50"
                    max="600"
                    placeholder="Es. 380"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = <strong>{{ formatCentimetersToMeters(form.height) }}</strong> (ponti/gallerie)
                </span>
              </div>

              <!-- Larghezza (cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  ↔️ {{ $t('pv.veicoli.field_width') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.width"
                    type="number"
                    step="1"
                    min="50"
                    max="400"
                    placeholder="Es. 255"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = <strong>{{ formatCentimetersToMeters(form.width) }}</strong> (strade strette)
                </span>
              </div>

              <!-- Lunghezza Totale (cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  📐 {{ $t('pv.veicoli.field_length') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.length"
                    type="number"
                    step="1"
                    min="100"
                    max="3500"
                    placeholder="Es. 1200"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = <strong>{{ formatCentimetersToMeters(form.length) }}</strong> (inclusi rimorchi)
                </span>
              </div>

              <!-- Peso Totale Max (gross_weight in kg) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  ⚖️ {{ $t('pv.veicoli.field_total_weight') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.gross_weight"
                    type="number"
                    step="10"
                    min="100"
                    max="100000"
                    placeholder="Es. 18000"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">kg</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = <strong>{{ formatKgToTons(form.gross_weight) }}</strong> (PTT Max pieno carico)
                </span>
              </div>

              <!-- Peso per Asse (weight_per_axle in kg) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🛞 {{ $t('pv.veicoli.field_axle_weight') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.weight_per_axle"
                    type="number"
                    step="10"
                    min="100"
                    max="30000"
                    placeholder="Es. 11500"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">kg</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = <strong>{{ formatKgToTons(form.weight_per_axle) }}</strong> (Max asse singolo)
                </span>
              </div>

              <!-- Altezza Sopra Primo Asse (height_above_first_axle in cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🏷️ Alt. Sopra 1° Asse (Pedaggi)
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.height_above_first_axle"
                    type="number"
                    step="1"
                    min="0"
                    max="500"
                    placeholder="Es. 130"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  Utilizzato per calcolo pedaggi autostradali
                </span>
              </div>

              <!-- Lunghezza Rimorchio (trailer_length in cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🚛 Lunghezza Rimorchio
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.trailer_length"
                    type="number"
                    step="1"
                    min="0"
                    max="2000"
                    placeholder="Es. 1360"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  = {{ formatCentimetersToMeters(form.trailer_length) }}
                </span>
              </div>

              <!-- Distanza Ralla-Asse Posteriore (kpra_length in cm) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  📐 Distanza Ralla-Asse (KPRA)
                </label>
                <div class="flex items-stretch border border-gray-300 rounded overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.kpra_length"
                    type="number"
                    step="1"
                    min="0"
                    max="1800"
                    placeholder="Es. 1200"
                    class="w-full min-w-0 text-xs p-2 font-bold border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">cm</span>
                </div>
                <span class="text-[10px] text-gray-500 block mt-1">
                  Kingpin-to-rear-axle distance
                </span>
              </div>

              <!-- Numero di Assi Totali -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🔢 {{ $t('pv.veicoli.field_axles_count') }}
                </label>
                <select
                  v-model.number="form.axle_count"
                  class="w-full text-xs p-2 font-bold border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                >
                  <option :value="2">2 Assi (Furgoni, Motrici 2 assi)</option>
                  <option :value="3">3 Assi (Motrici 3 assi, 26t)</option>
                  <option :value="4">4 Assi (Mezzi d'opera, 32t)</option>
                  <option :value="5">5 Assi (Bilici 44t / Autoarticolati)</option>
                  <option :value="6">6 o più Assi (Trasporti eccezionali)</option>
                </select>
                <span class="text-[10px] text-gray-500 block mt-1">Assi totali inclusi rimorchi</span>
              </div>

              <!-- Numero Rimorchi & Assi Rimorchio -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🔗 {{ $t('pv.veicoli.field_trailers_count') }}
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[9px] text-gray-500 block">N° Rimorchi</label>
                    <select
                      v-model.number="form.trailer_count"
                      class="w-full text-xs p-1.5 font-bold border border-gray-300 rounded"
                    >
                      <option :value="0">0</option>
                      <option :value="1">1</option>
                      <option :value="2">2</option>
                    </select>
                  </div>
                  <div>
                    <label class="text-[9px] text-gray-500 block">Assi Rimorchio</label>
                    <input
                      v-model.number="form.trailer_axle_count"
                      type="number"
                      min="0"
                      max="10"
                      class="w-full text-xs p-1.5 font-bold border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>

              <!-- Numero Pneumatici (tires_count) -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  🛞 Numero Pneumatici Totali
                </label>
                <input
                  v-model.number="form.tires_count"
                  type="number"
                  min="2"
                  max="40"
                  placeholder="Es. 4, 6, 10, 12..."
                  class="w-full text-xs p-2 font-bold border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
                <span class="text-[10px] text-gray-500 block mt-1">Gomme complessive a terra</span>
              </div>

              <!-- Pesi Aggiuntivi: Peso a Vuoto & Attuale -->
              <div class="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <label class="block text-xs font-bold text-blue-800 mb-1">
                  ⚖️ Peso a Vuoto / Attuale (kg)
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="text-[9px] text-gray-500 block">Tara a Vuoto</label>
                    <input
                      v-model.number="form.empty_weight"
                      type="number"
                      step="10"
                      placeholder="kg"
                      class="w-full text-xs p-1.5 font-bold border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label class="text-[9px] text-gray-500 block">Peso Attuale</label>
                    <input
                      v-model.number="form.current_weight"
                      type="number"
                      step="10"
                      placeholder="kg"
                      class="w-full text-xs p-1.5 font-bold border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB 3: CAPACITÀ E DOTAZIONI -->
          <div v-show="activeTab === 'capacity'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  📦 {{ $t('pv.veicoli.field_payload') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.payload_capacity"
                    type="number"
                    step="10"
                    placeholder="Es. 1400"
                    class="w-full min-w-0 text-xs p-2 border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">kg</span>
                </div>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  📐 {{ $t('pv.veicoli.field_cargo_volume') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.cargo_volume"
                    type="number"
                    step="0.5"
                    placeholder="Es. 15.0"
                    class="w-full min-w-0 text-xs p-2 border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">m³</span>
                </div>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  ⚡ {{ $t('pv.veicoli.field_max_speed') }}
                </label>
                <div class="flex items-stretch border border-gray-300 rounded shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <input
                    v-model.number="form.max_speed"
                    type="number"
                    step="5"
                    placeholder="Es. 90"
                    class="w-full min-w-0 text-xs p-2 border-0 focus:ring-0 focus:outline-none"
                  />
                  <span class="flex items-center px-2 bg-gray-100 border-l border-gray-300 text-[10px] font-bold text-gray-500 shrink-0">km/h</span>
                </div>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">
                  🌱 {{ $t('pv.veicoli.field_euro_class') }}
                </label>
                <select
                  v-model="form.euro_class"
                  class="w-full text-xs p-2 border border-gray-300 rounded shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Non specificata</option>
                  <option value="Euro 6">Euro 6 / VI (Moderno / ZTL)</option>
                  <option value="Euro 5">Euro 5 / V</option>
                  <option value="Euro 4">Euro 4 / IV</option>
                  <option value="Euro 3">Euro 3</option>
                  <option value="Elettrico">Elettrico (Full Electric)</option>
                  <option value="Ibrido">Ibrido / Metano / GNL</option>
                </select>
              </div>

              <!-- Checkbox Options -->
              <div class="col-span-1 md:col-span-2 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="form.has_tail_lift"
                    type="checkbox"
                    class="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div>
                    <span class="text-xs font-bold text-gray-900 block">
                      {{ $t('pv.veicoli.field_has_tail_lift') }}
                    </span>
                    <span class="text-[10px] text-gray-500">
                      Consente lo scarico autonomo presso clienti sprovvisti di muletto / rampa di carico
                    </span>
                  </div>
                </label>

                <div class="border-t border-gray-200 pt-3">
                  <label class="flex items-center gap-2 cursor-pointer mb-2">
                    <input
                      v-model="form.has_adr"
                      type="checkbox"
                      class="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <div>
                      <span class="text-xs font-bold text-gray-900 block">
                        {{ $t('pv.veicoli.field_has_adr') }}
                      </span>
                      <span class="text-[10px] text-gray-500">
                        Attiva i vincoli di transito per trasporto merci pericolose e restrizioni tunnel
                      </span>
                    </div>
                  </label>

                  <div v-if="form.has_adr" class="ml-6 mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div>
                      <label class="block text-[10px] font-bold text-amber-900 mb-0.5">
                        {{ $t('pv.veicoli.field_adr_tunnel_category') }}
                      </label>
                      <select
                        v-model="form.adr_tunnel_category"
                        class="w-full text-xs p-1.5 border border-amber-300 rounded bg-amber-50/50"
                      >
                        <option value="B">Categoria B (Restrizione minima)</option>
                        <option value="C">Categoria C</option>
                        <option value="D">Categoria D</option>
                        <option value="E">Categoria E (Restrizione massima)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- TAB 4: ANTEPRIMA SAGOMA (SVG BLUEPRINT) -->
          <div v-show="activeTab === 'preview'" class="space-y-4">
            <div class="text-xs text-gray-600">
              Rappresentazione schematica delle proporzioni del veicolo per i calcoli di ingombro geometrico:
            </div>

            <!-- SVG Blueprint Canvas -->
            <div class="p-4 sm:p-6 bg-slate-900 rounded-xl border border-slate-700 text-white flex flex-col items-center justify-center relative overflow-hidden shadow-inner">
              <div class="w-full max-w-lg">
                <svg viewBox="0 0 500 200" class="w-full h-auto drop-shadow-md">
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="1" />
                    </pattern>
                  </defs>
                  <rect width="500" height="200" fill="url(#grid)" />

                  <!-- Ground Line -->
                  <line x1="20" y1="165" x2="480" y2="165" stroke="#64748b" stroke-width="2" stroke-dasharray="4,4" />

                  <!-- Cab -->
                  <path
                    d="M 370 160 L 370 70 L 415 70 L 440 105 L 440 160 Z"
                    fill="#3b82f6"
                    stroke="#60a5fa"
                    stroke-width="2"
                  />
                  <!-- Windshield -->
                  <polygon points="410,75 433,105 385,105 385,75" fill="#93c5fd" opacity="0.8" />

                  <!-- Cargo Body -->
                  <rect
                    x="80"
                    y="45"
                    width="285"
                    height="115"
                    rx="4"
                    fill="#1e293b"
                    stroke="#38bdf8"
                    stroke-width="2"
                  />

                  <!-- Tail lift indicator (if present) -->
                  <rect
                    v-if="form.has_tail_lift"
                    x="68"
                    y="70"
                    width="10"
                    height="90"
                    fill="#10b981"
                    stroke="#34d399"
                    stroke-width="1.5"
                  />

                  <!-- Wheels -->
                  <circle cx="120" cy="165" r="15" fill="#0f172a" stroke="#cbd5e1" stroke-width="3" />
                  <circle cx="120" cy="165" r="6" fill="#94a3b8" />

                  <circle v-if="form.axle_count >= 3" cx="155" cy="165" r="15" fill="#0f172a" stroke="#cbd5e1" stroke-width="3" />
                  <circle v-if="form.axle_count >= 3" cx="155" cy="165" r="6" fill="#94a3b8" />

                  <circle v-if="form.axle_count >= 4" cx="190" cy="165" r="15" fill="#0f172a" stroke="#cbd5e1" stroke-width="3" />
                  <circle v-if="form.axle_count >= 4" cx="190" cy="165" r="6" fill="#94a3b8" />

                  <circle cx="410" cy="165" r="15" fill="#0f172a" stroke="#cbd5e1" stroke-width="3" />
                  <circle cx="410" cy="165" r="6" fill="#94a3b8" />

                  <!-- Dimension Lines -->
                  <line x1="45" y1="45" x2="45" y2="165" stroke="#f59e0b" stroke-width="1.5" />
                  <text x="35" y="110" fill="#fbbf24" font-size="10" font-weight="bold" text-anchor="end">H: {{ formatCentimetersToMeters(form.height) }}</text>

                  <line x1="80" y1="185" x2="440" y2="185" stroke="#f59e0b" stroke-width="1.5" />
                  <text x="260" y="196" fill="#fbbf24" font-size="10" font-weight="bold" text-anchor="middle">LUNGHEZZA: {{ formatCentimetersToMeters(form.length) }}</text>

                  <rect x="180" y="15" width="140" height="22" rx="11" fill="#f59e0b" opacity="0.9" />
                  <text x="250" y="30" fill="#000" font-size="10" font-weight="900" text-anchor="middle">PTT: {{ formatKgToTons(form.gross_weight) }}</text>
                </svg>
              </div>

              <!-- Quick Specs Summary Row under Graphic -->
              <div class="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 w-full text-center text-xs">
                <div class="bg-slate-800 p-2 rounded border border-slate-700">
                  <span class="text-[10px] text-slate-400 block uppercase font-bold">Altezza</span>
                  <span class="text-amber-400 font-black text-sm">{{ formatCentimetersToMeters(form.height) }}</span>
                </div>
                <div class="bg-slate-800 p-2 rounded border border-slate-700">
                  <span class="text-[10px] text-slate-400 block uppercase font-bold">Larghezza</span>
                  <span class="text-amber-400 font-black text-sm">{{ formatCentimetersToMeters(form.width) }}</span>
                </div>
                <div class="bg-slate-800 p-2 rounded border border-slate-700">
                  <span class="text-[10px] text-slate-400 block uppercase font-bold">Peso Totale PTT</span>
                  <span class="text-amber-400 font-black text-sm">{{ formatKgToTons(form.gross_weight) }}</span>
                </div>
                <div class="bg-slate-800 p-2 rounded border border-slate-700">
                  <span class="text-[10px] text-slate-400 block uppercase font-bold">Carico per Asse</span>
                  <span class="text-amber-400 font-black text-sm">{{ form.weight_per_axle ? formatKgToTons(form.weight_per_axle) : '---' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="p-3 sm:p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            @click="closeEditModal"
            type="button"
            class="px-4 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
          >
            {{ $t('actions.cancel') }}
          </button>
          <button
            @click="saveVehicle"
            :disabled="isSaving"
            type="button"
            class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded shadow transition-all flex items-center gap-1.5 disabled:opacity-50"
          >
            <v-icon v-if="!isSaving" name="hi-plus" scale="0.85" />
            <div v-else class="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full"></div>
            <span>{{ isSaving ? 'Salvataggio...' : $t('pv.veicoli.btn_save') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- MODAL: SAGOME PREDEFINITE / PRESET TEMPLATES -->
    <!-- ========================================================================= -->
    <div
      v-if="showTemplatesModal"
      class="fixed inset-0  z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      @click.self="showTemplatesModal = false"
    >
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
        <div class="bg-blue-600 text-white p-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <v-icon name="hi-lightning-bolt" scale="1.2" />
            <h2 class="text-base sm:text-lg font-bold">{{ $t('pv.veicoli.modal_templates_title') }}</h2>
          </div>
          <button @click="showTemplatesModal = false" class="text-white hover:text-gray-200">
            <v-icon name="co-x" scale="1.2" />
          </button>
        </div>

        <div class="p-4 sm:p-6 overflow-y-auto space-y-4">
          <p class="text-xs text-gray-600">
            {{ $t('pv.veicoli.templates_desc') }}
          </p>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="tpl in presetTemplates"
              :key="tpl.id"
              class="border border-gray-200 rounded-xl p-4 hover:border-blue-500 hover:shadow-md transition-all flex flex-col justify-between bg-white"
            >
              <div>
                <div class="flex items-center justify-between gap-2 mb-2">
                  <h4 class="font-bold text-sm text-gray-900">{{ tpl.name }}</h4>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold uppercase" :class="getTypeBadgeClass(tpl.type)">
                    {{ getTypeLabel(tpl.type) }}
                  </span>
                </div>
                <p class="text-xs text-gray-500 mb-3">{{ tpl.description }}</p>

                <div class="grid grid-cols-4 gap-1 text-center text-xs mb-3">
                  <div class="bg-gray-50 p-1.5 rounded">
                    <span class="text-[9px] text-gray-500 block uppercase">Alt</span>
                    <span class="font-bold">{{ formatCentimetersToMeters(tpl.height) }}</span>
                  </div>
                  <div class="bg-gray-50 p-1.5 rounded">
                    <span class="text-[9px] text-gray-500 block uppercase">Larg</span>
                    <span class="font-bold">{{ formatCentimetersToMeters(tpl.width) }}</span>
                  </div>
                  <div class="bg-gray-50 p-1.5 rounded">
                    <span class="text-[9px] text-gray-500 block uppercase">Lung</span>
                    <span class="font-bold">{{ formatCentimetersToMeters(tpl.length) }}</span>
                  </div>
                  <div class="bg-gray-50 p-1.5 rounded">
                    <span class="text-[9px] text-gray-500 block uppercase">PTT</span>
                    <span class="font-bold text-blue-700">{{ formatKgToTons(tpl.gross_weight) }}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                @click="createVehicleFromTemplate(tpl)"
                class="w-full mt-2 py-1.5 px-3 bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white rounded text-xs font-bold transition-colors flex items-center justify-center gap-1"
              >
                <v-icon name="hi-plus" scale="0.8" />
                <span>{{ $t('pv.veicoli.btn_use_template') }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            @click="showTemplatesModal = false"
            type="button"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs font-bold"
          >
            {{ $t('actions.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ========================================================================= -->
    <!-- MODAL: CONFERMA ELIMINAZIONE -->
    <!-- ========================================================================= -->
    <ConfirmationModal :show="showDeleteModal" @close="showDeleteModal = false">
      <template #title>
        {{ $t('pv.veicoli.confirm_delete_title') }}
      </template>

      <template #content>
        <p v-if="vehicleToDelete">
          {{ $t('pv.veicoli.confirm_delete_msg', { name: vehicleToDelete.name, plate: vehicleToDelete.plate }) }}
        </p>
      </template>

      <template #footer>
        <div class="flex justify-end gap-2">
          <button
            @click="showDeleteModal = false"
            type="button"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-xs font-bold text-gray-700"
          >
            {{ $t('actions.cancel') }}
          </button>
          <button
            @click="deleteVehicle"
            :disabled="isDeleting"
            type="button"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-xs font-bold text-white shadow disabled:opacity-50"
          >
            {{ isDeleting ? 'Eliminazione...' : $t('actions.delete') }}
          </button>
        </div>
      </template>
    </ConfirmationModal>
  </div>
</template>

<script>
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import axios from 'axios';

const STORAGE_KEY = 'p93_pv_vehicles_fleet_data';

// Standard demo vehicles (values in cm and kg)


export default {
  name: 'Veicoli',

  components: {
    BreadCrumbs,
    ConfirmationModal,
  },

  props: {
    initialVehicles: {
      type: Array,
      default: () => [],
    },
    carriers: {
      type: Array,
      default: () => [],
    },
    routing_provider: {
      type: String,
      default: 'google',
    },
    routing_provider_key: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('pv.breadcrumb.percorsi_vettori'), href: route('modules.home', 'percorsi-vettori') },
        { name: this.$t('pv.breadcrumb.veicoli'), href: '#' },
      ],
      vehicles: JSON.parse(JSON.stringify(this.initialVehicles)),
      searchQuery: '',
      filterType: '',
      filterCarrier: '',
      viewMode: 'cards', // 'cards' | 'table'
      isMobile: false,

      // Loading / Saving
      isSaving: false,
      isDeleting: false,

      // Modals
      showEditModal: false,
      isEditing: false,
      activeTab: 'general', // 'general' | 'dimensions' | 'capacity' | 'preview'
      showTemplatesModal: false,
      showDeleteModal: false,
      vehicleToDelete: null,

      // Vehicle Form State (values stored in cm and kg)
      form: this.getEmptyForm(),

      // Predefined Standard European Sagome (values in cm and kg)
      presetTemplates: [
        {
          id: 'tpl_van_standard',
          label: 'Furgone 3.5t Standard',
          name: 'Furgone 3.5t (Passo Lungo)',
          type: 'van',
          description: 'Furgone standard per consegne urbane (es. Ducato / Daily / Sprinter)',
          height: 255,
          width: 205,
          length: 599,
          trailer_length: null,
          kpra_length: null,
          height_above_first_axle: 120,
          gross_weight: 3500,
          current_weight: 3200,
          empty_weight: 2150,
          payload_capacity: 1350,
          weight_per_axle: 2100,
          axle_count: 2,
          trailer_axle_count: 0,
          trailer_count: 0,
          tires_count: 4,
          cargo_volume: 15.0,
          max_speed: 110,
          euro_class: 'Euro 6',
          has_tail_lift: false,
          has_adr: false,
          adr_tunnel_category: 'B',
        },
        {
          id: 'tpl_van_box_tail',
          label: 'Cabinato 3.5t con Sponda',
          name: 'Cabinato Gran Volume 3.5t con Sponda',
          type: 'van',
          description: 'Cabinato furgonato con sponda montacarichi posteriore (es. Daily Box)',
          height: 320,
          width: 220,
          length: 680,
          trailer_length: null,
          kpra_length: null,
          height_above_first_axle: 130,
          gross_weight: 3500,
          current_weight: 3400,
          empty_weight: 2600,
          payload_capacity: 900,
          weight_per_axle: 2600,
          axle_count: 2,
          trailer_axle_count: 0,
          trailer_count: 0,
          tires_count: 6,
          cargo_volume: 20.0,
          max_speed: 100,
          euro_class: 'Euro 6',
          has_tail_lift: true,
          has_adr: false,
          adr_tunnel_category: 'B',
        },
        {
          id: 'tpl_truck_18t',
          label: 'Motrice 2 Assi 18t',
          name: 'Motrice 2 Assi 18 Tonnellate',
          type: 'truck_medium',
          description: 'Autocarro medio per distribuzione regionale (es. Eurocargo / Atego 18t)',
          height: 365,
          width: 250,
          length: 880,
          trailer_length: null,
          kpra_length: null,
          height_above_first_axle: 145,
          gross_weight: 18000,
          current_weight: 16500,
          empty_weight: 8500,
          payload_capacity: 9500,
          weight_per_axle: 11500,
          axle_count: 2,
          trailer_axle_count: 0,
          trailer_count: 0,
          tires_count: 6,
          cargo_volume: 45.0,
          max_speed: 90,
          euro_class: 'Euro 6',
          has_tail_lift: true,
          has_adr: false,
          adr_tunnel_category: 'C',
        },
        {
          id: 'tpl_truck_26t',
          label: 'Motrice 3 Assi 26t',
          name: 'Motrice Pesante 3 Assi 26t',
          type: 'truck_heavy',
          description: 'Motrice 3 assi 6x2 per carichi pesanti e voluminosi (es. Stralis / Actros)',
          height: 385,
          width: 255,
          length: 1040,
          trailer_length: null,
          kpra_length: null,
          height_above_first_axle: 155,
          gross_weight: 26000,
          current_weight: 24000,
          empty_weight: 11000,
          payload_capacity: 15000,
          weight_per_axle: 11500,
          axle_count: 3,
          trailer_axle_count: 0,
          trailer_count: 0,
          tires_count: 8,
          cargo_volume: 60.0,
          max_speed: 90,
          euro_class: 'Euro 6',
          has_tail_lift: true,
          has_adr: false,
          adr_tunnel_category: 'D',
        },
        {
          id: 'tpl_articulated_44t',
          label: 'Bilico / Autoarticolato 44t',
          name: 'Autoarticolato 5 Assi (Bilico 44t)',
          type: 'articulated',
          description: 'Trattore Stradale + Semirimorchio standard 13.60m (es. Scania / MAN)',
          height: 400,
          width: 255,
          length: 1650,
          trailer_length: 1360,
          kpra_length: 1200,
          height_above_first_axle: 160,
          gross_weight: 44000,
          current_weight: 40000,
          empty_weight: 16000,
          payload_capacity: 28000,
          weight_per_axle: 11500,
          axle_count: 5,
          trailer_axle_count: 3,
          trailer_count: 1,
          tires_count: 12,
          cargo_volume: 90.0,
          max_speed: 85,
          euro_class: 'Euro 6',
          has_tail_lift: false,
          has_adr: false,
          adr_tunnel_category: 'E',
        },
      ],

      vehicleTypeOptions: [
        { value: 'van', label: 'Furgone / Leggero (≤ 3.5t)' },
        { value: 'truck_medium', label: 'Autocarro 2 Assi (3.5t - 18t)' },
        { value: 'truck_heavy', label: 'Motrice Pesante 3/4 Assi (> 18t)' },
        { value: 'articulated', label: 'Autoarticolato / Bilico (44t)' },
        { value: 'road_train', label: 'Autotreno (Motrice + Rimorchio)' },
        { value: 'custom', label: 'Altro / Sagoma Personalizzata' },
      ],
    };
  },

  computed: {
    carriersList() {
      if (this.carriers && this.carriers.length > 0) {
        return this.carriers.map(c => c.Carrier || c.carrier_name || c).filter(Boolean);
      }
      return ['BRT', 'GLS', 'DHL Freight', 'Fercam', 'TNT', 'SDA', 'Vettore Interno'];
    },

    filteredVehicles() {
      return this.vehicles.filter(v => {
        if (this.filterType && v.type !== this.filterType) return false;
        if (this.filterCarrier && v.carrier !== this.filterCarrier) return false;
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase().trim();
          const matchName = String(v.name || '').toLowerCase().includes(q);
          const matchPlate = String(v.plate || '').toLowerCase().includes(q);
          const matchModel = String(v.brand_model || '').toLowerCase().includes(q);
          const matchCarrier = String(v.carrier || '').toLowerCase().includes(q);
          if (!matchName && !matchPlate && !matchModel && !matchCarrier) return false;
        }
        return true;
      });
    },

    countHeavyVehicles() {
      return this.vehicles.filter(v => (v.gross_weight || 0) > 3500).length;
    },

    countLightVehicles() {
      return this.vehicles.filter(v => (v.gross_weight || 0) <= 3500).length;
    },

    countSpecialVehicles() {
      return this.vehicles.filter(v => v.has_adr || v.has_tail_lift).length;
    },

    effectiveViewMode() {
      return this.isMobile ? 'cards' : this.viewMode;
    },
  },

  mounted() {
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.checkIsMobile);
  },

  methods: {
    checkIsMobile() {
      this.isMobile = window.innerWidth < 640;
    },

    getEmptyForm() {
      return {
        id: null,
        name: '',
        plate: '',
        type: 'van',
        carrier: '',
        brand_model: '',
        height: 255, // cm
        width: 205, // cm
        length: 599, // cm
        trailer_length: null,
        kpra_length: null,
        height_above_first_axle: null,
        gross_weight: 3500, // kg
        current_weight: null,
        empty_weight: null,
        payload_capacity: 1350,
        weight_per_axle: 2000,
        weight_per_axle_group: null,
        axle_count: 2,
        trailer_axle_count: 0,
        trailer_count: 0,
        tires_count: 4,
        cargo_volume: 15.0,
        max_speed: 100,
        euro_class: 'Euro 6',
        has_tail_lift: false,
        has_adr: false,
        adr_tunnel_category: 'B',
        is_active: true,
        notes: '',
      };
    },


    saveFleetToStorage() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.vehicles));
      } catch (e) {
        console.error('Errore salvataggio localStorage per flotta veicoli:', e);
      }
    },

    resetFilters() {
      this.searchQuery = '';
      this.filterType = '';
      this.filterCarrier = '';
    },

    openCreateModal() {
      this.isEditing = false;
      this.activeTab = 'general';
      this.form = this.getEmptyForm();
      this.showEditModal = true;
    },

    openEditModal(vehicle) {
      this.isEditing = true;
      this.activeTab = 'general';
      this.form = JSON.parse(JSON.stringify(vehicle));
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.form = this.getEmptyForm();
    },

    applyTemplateToForm(tpl) {
      this.form.type = tpl.type;
      this.form.height = tpl.height;
      this.form.width = tpl.width;
      this.form.length = tpl.length;
      this.form.trailer_length = tpl.trailer_length;
      this.form.kpra_length = tpl.kpra_length;
      this.form.height_above_first_axle = tpl.height_above_first_axle;
      this.form.gross_weight = tpl.gross_weight;
      this.form.current_weight = tpl.current_weight;
      this.form.empty_weight = tpl.empty_weight;
      this.form.payload_capacity = tpl.payload_capacity;
      this.form.weight_per_axle = tpl.weight_per_axle;
      this.form.axle_count = tpl.axle_count;
      this.form.trailer_axle_count = tpl.trailer_axle_count;
      this.form.trailer_count = tpl.trailer_count;
      this.form.tires_count = tpl.tires_count;
      this.form.cargo_volume = tpl.cargo_volume;
      this.form.max_speed = tpl.max_speed;
      this.form.euro_class = tpl.euro_class;
      this.form.has_tail_lift = tpl.has_tail_lift;
      this.form.has_adr = tpl.has_adr;
      this.form.adr_tunnel_category = tpl.adr_tunnel_category;
      if (!this.form.name) {
        this.form.name = tpl.name;
      }
    },

    async saveVehicle() {
      if (!this.form.name || !this.form.plate) {
        alert('Compila il Nome e la Targa del veicolo.');
        return;
      }

      this.form.plate = String(this.form.plate).toUpperCase().trim();
      this.isSaving = true;

      try {
        const response = await axios.post(route('modules.percorsi-vettori.veicoli.save'), this.form);
        const saved = response.data?.vehicle;

        if (saved) {
          if (this.isEditing && this.form.id) {
            const index = this.vehicles.findIndex(v => v.id === this.form.id);
            if (index !== -1) {
              this.vehicles.splice(index, 1, saved);
            }
          } else {
            this.vehicles.unshift(saved);
          }
        } else {
          // Fallback locale
          if (this.isEditing && this.form.id) {
            const index = this.vehicles.findIndex(v => v.id === this.form.id);
            if (index !== -1) {
              this.vehicles.splice(index, 1, JSON.parse(JSON.stringify(this.form)));
            }
          } else {
            const newVehicle = {
              ...JSON.parse(JSON.stringify(this.form)),
              id: Date.now(),
            };
            this.vehicles.unshift(newVehicle);
          }
        }

        this.saveFleetToStorage();
        this.closeEditModal();
      } catch (e) {
        console.error('Errore salvataggio backend veicolo:', e);
        // Salvataggio locale in caso di errore di connessione
        if (this.isEditing && this.form.id) {
          const index = this.vehicles.findIndex(v => v.id === this.form.id);
          if (index !== -1) {
            this.vehicles.splice(index, 1, JSON.parse(JSON.stringify(this.form)));
          }
        } else {
          const newVehicle = {
            ...JSON.parse(JSON.stringify(this.form)),
            id: Date.now(),
          };
          this.vehicles.unshift(newVehicle);
        }
        this.saveFleetToStorage();
        this.closeEditModal();
      } finally {
        this.isSaving = false;
      }
    },

    async duplicateVehicle(vehicle) {
      const copy = JSON.parse(JSON.stringify(vehicle));
      delete copy.id;
      copy.name = `${copy.name} (Copia)`;
      copy.plate = `${copy.plate ? copy.plate.slice(0, 5) : 'AA000'}CP`;

      try {
        const response = await axios.post(route('modules.percorsi-vettori.veicoli.save'), copy);
        const saved = response.data?.vehicle || { ...copy, id: Date.now() };
        this.vehicles.unshift(saved);
        this.saveFleetToStorage();
      } catch (e) {
        copy.id = Date.now();
        this.vehicles.unshift(copy);
        this.saveFleetToStorage();
      }
    },

    openTemplatesModal() {
      this.showTemplatesModal = true;
    },

    async createVehicleFromTemplate(tpl) {
      const newVehicle = {
        name: tpl.name,
        plate: `TPL${Math.floor(100 + Math.random() * 900)}XX`,
        type: tpl.type,
        carrier: '',
        brand_model: tpl.name,
        height: tpl.height,
        width: tpl.width,
        length: tpl.length,
        trailer_length: tpl.trailer_length,
        kpra_length: tpl.kpra_length,
        height_above_first_axle: tpl.height_above_first_axle,
        gross_weight: tpl.gross_weight,
        current_weight: tpl.current_weight,
        empty_weight: tpl.empty_weight,
        payload_capacity: tpl.payload_capacity,
        weight_per_axle: tpl.weight_per_axle,
        axle_count: tpl.axle_count,
        trailer_axle_count: tpl.trailer_axle_count,
        trailer_count: tpl.trailer_count,
        tires_count: tpl.tires_count,
        cargo_volume: tpl.cargo_volume,
        max_speed: tpl.max_speed,
        euro_class: tpl.euro_class,
        has_tail_lift: tpl.has_tail_lift,
        has_adr: tpl.has_adr,
        adr_tunnel_category: tpl.adr_tunnel_category,
        is_active: true,
        notes: tpl.description,
      };

      try {
        const response = await axios.post(route('modules.percorsi-vettori.veicoli.save'), newVehicle);
        const saved = response.data?.vehicle || { ...newVehicle, id: Date.now() };
        this.vehicles.unshift(saved);
      } catch (e) {
        newVehicle.id = Date.now();
        this.vehicles.unshift(newVehicle);
      }

      this.saveFleetToStorage();
      this.showTemplatesModal = false;
    },

    confirmDelete(vehicle) {
      this.vehicleToDelete = vehicle;
      this.showDeleteModal = true;
    },

    async deleteVehicle() {
      if (!this.vehicleToDelete) return;
      this.isDeleting = true;

      try {
        if (this.vehicleToDelete.id) {
          await axios.delete(route('modules.percorsi-vettori.veicoli.delete', this.vehicleToDelete.id));
        }
      } catch (e) {
        console.warn('Eliminazione su server non riuscita, eliminazione locale:', e);
      } finally {
        this.vehicles = this.vehicles.filter(v => v.id !== this.vehicleToDelete.id);
        this.saveFleetToStorage();
        this.showDeleteModal = false;
        this.vehicleToDelete = null;
        this.isDeleting = false;
      }
    },

    formatCentimetersToMeters(valInCm) {
      if (valInCm === null || valInCm === undefined || isNaN(valInCm)) return '---';
      const meters = Number(valInCm) / 100;
      return `${meters.toFixed(2)} m`;
    },

    formatKgToTons(valInKg) {
      if (valInKg === null || valInKg === undefined || isNaN(valInKg)) return '---';
      const tons = Number(valInKg) / 1000;
      return `${tons.toFixed(1)} t`;
    },

    getTypeLabel(type) {
      const found = this.vehicleTypeOptions.find(o => o.value === type);
      return found ? found.label : (type || 'Standard');
    },

    getTypeBadgeClass(type) {
      switch (type) {
        case 'van':
          return 'bg-green-100 text-green-800 border border-green-200';
        case 'truck_medium':
          return 'bg-blue-100 text-blue-800 border border-blue-200';
        case 'truck_heavy':
          return 'bg-orange-100 text-orange-800 border border-orange-200';
        case 'articulated':
        case 'road_train':
          return 'bg-purple-100 text-purple-800 border border-purple-200';
        default:
          return 'bg-gray-100 text-gray-800 border border-gray-200';
      }
    },
  },
};
</script>

<style scoped>
/* Scoped custom styling */
</style>
